import { extractRequiredIcons } from '../../services/iconService';

export const STATUS_LABELS = { today: 'Today', past: 'Published', ready: 'Ready', draft: 'Draft', empty: 'Empty' };

// Cells covered by a word, as "row-col" keys.
export const wordCells = (word) => {
  const cells = new Set();
  if (!word) return cells;
  for (let i = 0; i < word.answer.length; i++) {
    const row = word.direction === 'across' ? word.startRow : word.startRow + i;
    const col = word.direction === 'across' ? word.startCol + i : word.startCol;
    cells.add(`${row}-${col}`);
  }
  return cells;
};

// The grid, clue texts and size a puzzle opens with. generatedGrid (rows of a grid picked in the
// generator, '■' = black) replaces the day's grid and clears the clues.
export function initialGridState(puzzle, generatedGrid) {
  const gridHeight = puzzle?.gridSize?.height || 7;
  const gridWidth = puzzle?.gridSize?.width || 6;
  let size = { width: gridWidth, height: gridHeight };
  const clues = {};

  const newGrid = Array(gridHeight).fill().map(() =>
    Array(gridWidth).fill().map(() => ({ letter: '', isBlack: false }))
  );

  if (puzzle?.words) {
    puzzle.blackSquares?.forEach(({ row, col }) => {
      if (newGrid[row]?.[col]) newGrid[row][col].isBlack = true;
    });

    puzzle.words.forEach(word => {
      for (let i = 0; i < word.answer.length; i++) {
        const row = word.direction === 'across' ? word.startRow : word.startRow + i;
        const col = word.direction === 'across' ? word.startCol + i : word.startCol;
        if (newGrid[row]?.[col]) newGrid[row][col].letter = word.answer[i];
      }
    });

    size = puzzle.gridSize || size;

    puzzle.words.forEach(word => {
      // Convert soft hyphens to placeholder character for editing
      clues[word.id] = (word.clue || '').replace(/­/g, '︲');
    });
  }

  if (generatedGrid?.length) {
    const rows = generatedGrid.map(line => Array.from(line));
    const width = Math.max(...rows.map(r => r.length));
    return {
      grid: rows.map(r => Array.from({ length: width }, (_, c) =>
        r[c] && r[c] !== '■' ? { letter: r[c], isBlack: false } : { letter: '', isBlack: true })),
      size: { width, height: rows.length },
      clues: {},
      unsaved: true,
    };
  }
  return { grid: newGrid, size, clues, unsaved: false };
}

// Words of 2+ letters, across then down, each keeping the clue typed for its id.
// Returns null while there is no grid yet.
export function findWords(grid, clues, size) {
  if (!grid.length) return null;

  const detected = [];
  let wordId = 1;

  // Detect horizontal words (across)
  for (let row = 0; row < grid.length; row++) {
    if (!grid[row]) continue;
    let word = '', startCol = -1;

    for (let col = 0; col <= grid[row].length; col++) {
      const cell = col < grid[row].length && grid[row][col] ? grid[row][col] : { isBlack: true };

      if (!cell.isBlack && cell.letter) {
        if (word === '') startCol = col;
        word += cell.letter;
      } else {
        if (word.length >= 2) {
          detected.push({
            id: wordId++,
            answer: word.toUpperCase(),
            direction: 'across',
            startRow: row,
            startCol: startCol,
            clue: clues[wordId - 1] || ''
          });
        }
        word = '';
      }
    }
  }

  // Detect vertical words (down)
  for (let col = 0; col < size.width; col++) {
    let word = '', startRow = -1;

    for (let row = 0; row <= grid.length; row++) {
      const cell = row < grid.length && grid[row]?.[col] ? grid[row][col] : { isBlack: true };

      if (!cell.isBlack && cell.letter) {
        if (word === '') startRow = row;
        word += cell.letter;
      } else {
        if (word.length >= 2) {
          detected.push({
            id: wordId++,
            answer: word.toUpperCase(),
            direction: 'down',
            startRow: startRow,
            startCol: col,
            clue: clues[wordId - 1] || ''
          });
        }
        word = '';
      }
    }
  }

  return detected;
}

// Mirror Flutter's _calculateClaimedCells: each clue cell may "claim" adjacent
// empty border cells (out-of-bounds/black with no clue) so its contents can
// visually expand horizontally. Returns a map "dRow,dCol" -> ownerKey.
export function computeClaimedCells(size, grid, words, clues) {
  const displayWidth = size.width + 1;
  const displayHeight = size.height + 1;
  const isOOB = (r, c) => r < 0 || c < 0 || r >= size.height || c >= size.width;
  const isBlack = (r, c) => !isOOB(r, c) && grid[r]?.[c]?.isBlack;
  const wordStart = (r, c, dir) => words.find(w => w.direction === dir && w.startRow === r && w.startCol === c);

  const clueCells = [];
  for (let dRow = 0; dRow < displayHeight; dRow++) {
    for (let dCol = 0; dCol < displayWidth; dCol++) {
      const r = dRow - 1, c = dCol - 1;
      if (isOOB(r, c) || isBlack(r, c)) {
        const word = wordStart(r, c + 1, 'across') || wordStart(r + 1, c, 'down');
        if (word) clueCells.push({ dRow, dCol, clue: clues[word.id] || '', wordId: word.id });
      }
    }
  }

  const emptyCells = {};
  for (const clue of clueCells) {
    for (const [dr, dc] of [[0, 1], [0, -1]]) {
      const r = clue.dRow + dr, c = clue.dCol + dc;
      if (r < 0 || r >= displayHeight || c < 0 || c >= displayWidth) continue;
      const gR = r - 1, gC = c - 1;
      if (!(isOOB(gR, gC) || isBlack(gR, gC))) continue;
      if (wordStart(gR, gC + 1, 'across') || wordStart(gR + 1, gC, 'down')) continue;
      const key = `${r},${c}`;
      (emptyCells[key] = emptyCells[key] || []).push(clue);
    }
  }

  const sorted = Object.entries(emptyCells).sort((a, b) => {
    if (a[1].length !== b[1].length) return a[1].length - b[1].length;
    const aMax = Math.max(...a[1].map(c => c.clue.length));
    const bMax = Math.max(...b[1].map(c => c.clue.length));
    return bMax - aMax;
  });

  const claimed = {};
  const counts = {};
  for (const [key, list] of sorted) {
    if (!list.length) continue;
    list.sort((a, b) => {
      const aKey = `${a.dRow},${a.dCol}`, bKey = `${b.dRow},${b.dCol}`;
      const aCnt = counts[aKey] || 0, bCnt = counts[bKey] || 0;
      return aCnt !== bCnt ? aCnt - bCnt : b.clue.length - a.clue.length;
    });
    const winner = list[0];
    const ownerKey = `${winner.dRow},${winner.dCol}`;
    claimed[key] = ownerKey;
    counts[ownerKey] = (counts[ownerKey] || 0) + 1;
  }
  return claimed;
}

export function validatePuzzle(words, grid, clues) {
  const errors = [];

  const hasEmptyCells = words.some(word => {
    for (let i = 0; i < word.answer.length; i++) {
      const row = word.direction === 'across' ? word.startRow : word.startRow + i;
      const col = word.direction === 'across' ? word.startCol + i : word.startCol;
      if (!grid[row]?.[col]?.letter) return true;
    }
    return false;
  });

  if (hasEmptyCells) errors.push('Some words have empty cells');

  const missingClues = words.filter(word => !clues[word.id]?.trim());
  if (missingClues.length) {
    errors.push(`${missingClues.length} word(s) missing clues: ${missingClues.map(w => w.answer).join(', ')}`);
  }

  if (!words.length) errors.push('No words detected in grid');

  return errors;
}

// Imports a pasted crossword (■ = black square) over the grid; null if there is nothing to paste
export function pasteIntoGrid(grid, text) {
  const lines = text.trim().split('\n').filter(line => line.trim());
  if (!lines.length) return null;

  const validChars = /[A-ZÅÄÖ]/i;
  const blackChars = ['■', '█', '#', '.', ' '];

  const newGrid = grid.map(row => [...row]);

  lines.forEach((line, row) => {
    if (row >= newGrid.length) return;
    Array.from(line).forEach((char, col) => {
      if (col >= newGrid[0].length) return;

      if (blackChars.includes(char)) {
        newGrid[row][col] = { ...newGrid[row][col], isBlack: true, letter: '' };
      } else if (validChars.test(char)) {
        newGrid[row][col] = { ...newGrid[row][col], letter: char.toUpperCase(), isBlack: false };
      }
    });
  });

  return newGrid;
}

export function resizeGrid(grid, size, dimension, value) {
  return dimension === 'width'
    ? Array(size.height).fill().map((_, r) =>
        Array(value).fill().map((_, c) =>
          grid[r]?.[c] ? { ...grid[r][c] } : { letter: '', isBlack: false }
        )
      )
    : Array(value).fill().map((_, r) =>
        Array(size.width).fill().map((_, c) =>
          grid[r]?.[c] ? { ...grid[r][c] } : { letter: '', isBlack: false }
        )
      );
}

// Size, black squares and typed letters handed to the generator as its starting point
export function generatorStart(grid, size) {
  const black = [], letters = {};
  grid.forEach((row, r) => row.forEach((cell, c) => {
    const pos = r * size.width + c;
    if (cell.isBlack) black.push(pos);
    else if (/^[A-ZÄÖ]$/.test(cell.letter)) letters[pos] = cell.letter;
  }));
  return { w: size.width, h: size.height, black, letters };
}

// The Firestore document for a save; placeholder hyphens become real soft hyphens.
export function buildPuzzleData({ puzzle, size, grid, words, clues, isSpecialDate, specialTitle, specialName, notificationTitle, notificationBody }) {
  const blacks = [];
  grid.forEach((row, r) => {
    row.forEach((cell, c) => {
      if (cell.isBlack) blacks.push({ row: r, col: c });
    });
  });

  const finalWords = words.map(word => ({
    ...word,
    // Convert placeholder characters back to soft hyphens for Firestore
    clue: (clues[word.id] || '').replace(/︲/g, '­')
  }));

  // Build special date object if any fields have content. Unticking the box drops both
  // the special date and its notification on save.
  const specialDateObj = {};
  if (isSpecialDate && (specialTitle.trim() || specialName.trim())) {
    if (specialTitle.trim()) specialDateObj.title = specialTitle.trim();
    if (specialName.trim()) specialDateObj.name = specialName.trim();
  }

  // Build notification object if any fields have content
  const notificationObj = {};
  if (isSpecialDate && (notificationTitle.trim() || notificationBody.trim())) {
    if (notificationTitle.trim()) notificationObj.title = notificationTitle.trim();
    if (notificationBody.trim()) notificationObj.body = notificationBody.trim();
  }

  // Extract required custom icons from clues
  const requiredIcons = extractRequiredIcons({ words: finalWords });

  return {
    id: puzzle.id,
    gridSize: size,
    words: finalWords,
    blackSquares: blacks,
    published: puzzle.published,
    isToday: puzzle.isToday || false,
    requiredIcons,
    contentVersion: window.crypto.randomUUID(), // Generate new version on each save using browser crypto API
    ...(Object.keys(specialDateObj).length > 0 && { specialDate: specialDateObj }),
    ...(Object.keys(notificationObj).length > 0 && { specialDateNotification: notificationObj })
  };
}
